import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "../common/PageTitle";
import { connectorService } from "../../api/services/connectorService";
import type { ConnectorProviderTileConfig } from "../../utils/connectorDashboardConfig";
import { CONNECTORS_DASHBOARD_PATH } from "../../utils/connectorRoutes";

interface AddConnectorFormProps {
  provider: ConnectorProviderTileConfig;
  tenantId: string | null;
}

const AddConnectorForm: React.FC<AddConnectorFormProps> = ({
  provider,
  tenantId,
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [organization, setOrganization] = useState("");
  const [environment, setEnvironment] = useState("");
  const [managementApiUrl, setManagementApiUrl] = useState(
    provider.defaultManagementApiUrl,
  );
  const [description, setDescription] = useState("");
  const [gcpProjectId, setGcpProjectId] = useState("");
  const [runtimeRegion, setRuntimeRegion] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!name.trim() || !organization.trim() || !managementApiUrl.trim()) {
      setErrorMessage("Name, organization, and management API URL are required.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    try {
      await connectorService.createConnector(
        {
          providerType: provider.id,
          name: name.trim(),
          organization: organization.trim(),
          environment: environment.trim() || undefined,
          managementApiUrl: managementApiUrl.trim(),
          description: description.trim() || undefined,
          gcpProjectId: provider.showGcpFields
            ? gcpProjectId.trim() || undefined
            : undefined,
          runtimeRegion: provider.showRuntimeRegion
            ? runtimeRegion.trim() || undefined
            : undefined,
        },
        tenantId,
      );
      setSuccessOpen(true);
      setTimeout(() => navigate(CONNECTORS_DASHBOARD_PATH), 800);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to create connector";
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle={`Add ${provider.title} Connector`}
        pageDescription={`Register a new ${provider.title} API management integration.`}
      />
      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper
          sx={{
            mt: 3,
            mb: 3,
            p: 3,
            maxWidth: 640,
            border: "1px solid #e0e0e0",
          }}
        >
          <Stack spacing={3}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

            <TextField
              label="Connector name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
              helperText="A friendly label for this integration"
            />
            <TextField
              label="Organization"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
              fullWidth
              required
              helperText="Apigee organization name"
            />
            <TextField
              label="Environment"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              fullWidth
              helperText="Optional default environment (e.g. prod, test)"
            />
            <TextField
              label="Management API URL"
              value={managementApiUrl}
              onChange={(e) => setManagementApiUrl(e.target.value)}
              fullWidth
              required
            />
            {provider.showGcpFields && (
              <TextField
                label="GCP project ID"
                value={gcpProjectId}
                onChange={(e) => setGcpProjectId(e.target.value)}
                fullWidth
                helperText="Google Cloud project linked to this Apigee org"
              />
            )}
            {provider.showRuntimeRegion && (
              <TextField
                label="Runtime region"
                value={runtimeRegion}
                onChange={(e) => setRuntimeRegion(e.target.value)}
                fullWidth
                helperText="Hybrid runtime region (e.g. us-central1)"
              />
            )}
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(CONNECTORS_DASHBOARD_PATH)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Saving…" : "Add connector"}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      <Snackbar
        open={successOpen}
        autoHideDuration={4000}
        onClose={() => setSuccessOpen(false)}
        message="Connector created successfully"
      />
    </Box>
  );
};

export default AddConnectorForm;
